import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import ProductListItem from '../../components/ProductListItem';
import { RangeControl, PublisherControl, BisacControl } from '../../components/InputControls';
import { makeRequest, firstFeaturedIndex, formatISODate } from '../../Utils';
import moment from 'moment';
import './style.css';
import Loader from './Loader';
import GhostGrid from '../../components/GhostGrid';
import { exportProducts } from './export';

export default function BooksAll({ mode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [featured, setFeatured] = useState(-1);
  const [publisher, setPublisher] = useState(searchParams.get('pub') || '');
  const [minPages, setMinPages] = useState(searchParams.get('minPages') ? parseInt(searchParams.get('minPages')) : 1);
  const [maxPages, setMaxPages] = useState(searchParams.get('maxPages') ? parseInt(searchParams.get('maxPages')) : 2000);
  const [formats, setFormats] = useState(searchParams.get('formats') ? searchParams.get('formats').split(',') : []);
  const [bisac, setBisac] = useState(searchParams.get('bisac') || '');
  const [categories, setCategories] = useState(searchParams.get('categories') ? searchParams.get('categories').split(',') : []);
  const [hasPreview, setHasPreview] = useState(searchParams.get('hasPreview') || false);
  const [inStockNow, setInStockNow] = useState(searchParams.get('inStockNow') || false);
  const [onSale, setOnSale] = useState(searchParams.get('onSale') || false);
  const [hasMore, setHasMore] = useState(true);
  const [lastOne, setLastOne] = useState(searchParams.get('lastOne') || false);
  const [used, setUsed] = useState(searchParams.get('hasUsed') || false);
  const [page, setPage] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [allFormats, setAllFormats] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  const per = 20;

  document.title = `Browse All${mode === 'new' ? ' New ' : mode === 'upcoming' ? ' Upcoming ' : mode === 'ebooks' ? ' e' : ' '}Books | Asterism Books`;
  document.querySelector('meta[name="description"]').setAttribute('content', `Browse and search through our entire product catalog of small press books!`);

  useEffect(() => {
    let featuredIndex = firstFeaturedIndex(products);
    setFeatured(featuredIndex);
  }, [products])

  const debounceDelay = 750;
  const debounceTimer = useRef(0);
  const controller = useRef();

  const location = useLocation();

  const getOptions = async () => {
    let formatData = await makeRequest('options?id=formats');
    setAllFormats(formatData.formats);
    try {
      let categoryData = await makeRequest('options?id=categories');
      setAllCategories(categoryData.categories.sort((a, b) => { return a.value.localeCompare(b.value) }))
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    getOptions();
    setPage(0);
    setHasMore(false);
  }, []);

  const getNew = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatISODate(tomorrow);
  }

  const getUpcoming = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatISODate(tomorrow);
  }

  const getProducts = async (more = false) => {
    clearTimeout(debounceTimer.current);
    controller.current?.abort();
    controller.current = new AbortController();
    setLoading(more ? false : true);
    debounceTimer.current = setTimeout(async () => {
      let urlAppend = window.location.search && more ? `&page=${page}` : more ? `?page=${page}` : '';
      if (mode === 'new') {
        let newDate = getNew();
        urlAppend += (urlAppend || window.location.search) ? `&beforeDate=${newDate}` : `?beforeDate=${newDate}`;
      } else if (mode === 'upcoming') {
        let upcomingDate = getUpcoming();
        urlAppend += (urlAppend || window.location.search) ? `&afterDate=${upcomingDate}&ascending=true` : `?afterDate=${upcomingDate}&ascending=true`;
      }
      let data = await makeRequest(`all-products${window.location.search}${urlAppend}`, 'GET');
      setProducts(more ? products.concat(data.docs) : data.docs); // Either add new stuff to the array or set the new products
      setPage(page + 1);
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }, debounceDelay)
  }

  useEffect(() => {
    setHasMore(page * per === products.length);
  }, [products, page]);

  useEffect(() => {
    if (mode === 'ebooks') {
      setFormats(['ebookPDF', 'ebookEPUB', 'ebookMulti']);
    }
  }, []);

  const toggleCat = (value) => {
    let newCats = [...categories];
    let i = categories.indexOf(value);
    if (i !== -1) {
      newCats.splice(i, 1);
    } else {
      newCats.push(value);
    }
    setCategories(newCats);
  }

  const toggleFormat = (value) => {
    let newFormats = [...formats];
    let i = formats.indexOf(value);
    if (i !== -1) {
      newFormats.splice(i, 1);
    } else {
      newFormats.push(value);
    }
    setFormats(newFormats)
  }

  useEffect(() => {
    setPage(0);
  }, [location])

  useEffect(() => {
    getProducts();
  }, [categories, formats, bisac, used, inStockNow, lastOne, publisher, minPages, maxPages]);

  useEffect(() => {
    if (page === 0) {
      getProducts();
    }
  }, [page])

  useEffect(() => {
    if (searchParams.get('formats')) {
      setFormats(searchParams.get('formats').split(','))
    }
    if (searchParams.get('categories')) {
      setCategories(searchParams.get('categories').split(','))
    }
  }, []);

  useEffect(() => {
    if (publisher) {
      searchParams.set('pub', publisher)
    } else {
      searchParams.delete('pub');
    }
    setSearchParams(searchParams);
  }, [publisher, searchParams, setSearchParams]);

  useEffect(() => {
    if (!onSale) {
      searchParams.delete('onSale');
    } else {
      searchParams.set('onSale', 'true');
    }
    setSearchParams(searchParams);
  }, [onSale, searchParams, setSearchParams])

  useEffect(() => {
    if (!used) {
      searchParams.delete('hasUsed');
    } else {
      searchParams.set('hasUsed', 'true');
    }
    setSearchParams(searchParams);
  }, [used, searchParams, setSearchParams])

  useEffect(() => {
    if (!bisac) {
      searchParams.delete('bisac');
    } else {
      searchParams.set('bisac', bisac);
    }
    setSearchParams(searchParams);
  }, [bisac, searchParams, setSearchParams])

  useEffect(() => {
    if (inStockNow) {
      searchParams.set('inStockNow', true)
    } else {
      searchParams.delete('inStockNow');
    }
    setSearchParams(searchParams);
  }, [inStockNow, searchParams, setSearchParams]);

  useEffect(() => {
    if (hasPreview) {
      searchParams.set('hasPreview', true)
    } else {
      searchParams.delete('hasPreview');
    }
    setSearchParams(searchParams);
  }, [hasPreview, searchParams, setSearchParams]);

  useEffect(() => {
    if (lastOne) {
      searchParams.set('lastOne', true)
    } else {
      searchParams.delete('lastOne');
    }
    setSearchParams(searchParams);
  }, [lastOne, searchParams, setSearchParams]);

  useEffect(() => {
    if (formats && formats.length > 0) {
      searchParams.set('formats', formats.join(','))
    } else {
      searchParams.delete('formats');
    }
    setSearchParams(searchParams);
  }, [formats, searchParams, setSearchParams]);

  useEffect(() => {
    if (categories && categories.length > 0) {
      searchParams.set('categories', categories.join(','))
    } else {
      searchParams.delete('categories');
    }
    setSearchParams(searchParams);
  }, [categories, searchParams, setSearchParams]);

  useEffect(() => {
    if (minPages && minPages > 1) {
      searchParams.set('minPages', minPages)
    } else {
      searchParams.delete('minPages');
    }
    setSearchParams(searchParams);
  }, [minPages, searchParams, setSearchParams]);

  useEffect(() => {
    if (maxPages && maxPages < 2000) {
      searchParams.set('maxPages', maxPages)
    } else {
      searchParams.delete('maxPages');
    }
    setSearchParams(searchParams);
  }, [maxPages, searchParams, setSearchParams]);

  const exportView = () => {
    let data = exportProducts(products);
    const hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(data);
    hiddenElement.target = '_blank';
    hiddenElement.download = `asterism-exported-view-${moment().format('DD-MM-YYYY')}.csv`;
    hiddenElement.click();
  }

  return (
    <div>
      <div className='flex-layout pageHeadContainer roomAboveMedium roomBelowMedium'>
        <h1 className='page-header'>
          {`All${mode === 'new' ? ' New ' : mode === 'upcoming' ? ' Upcoming ' : mode === 'ebooks' ? 'e' : ' '}Books`}
        </h1>
        <button onClick={() => { setFiltersOpen(true) }} className='buttonSecondary buttonSecondarySmall filterMobile'><i className='fa-solid fa-filter'></i> Filter</button>
      </div>
      <div className='filteredGrid'>
        <div className={`filterPanel ${filtersOpen && 'filterPanelOpen'}`}>
          <div className='roomBelowLarge filterSticky'><button onClick={() => { setFiltersOpen(false) }} className='buttonSecondary buttonSecondarySmall filterMobile'>Close Filters</button></div>
          <div className='sidebarFilter'>
            <PublisherControl
              label='Publisher'
              value={publisher}
              change={setPublisher}
            /></div>

          <div className='sidebarFilter'>
            <BisacControl
              value={bisac}
              change={setBisac}
              label={'BISAC Code'}
              help={''}
            />
          </div>
          <div className='sidebarFilter'>
            <div className='pseudoLabel'>Formats</div>
            <div className='catList'>
              {allFormats.map((format) => {
                if (mode !== 'ebooks' || format.value.includes('ebook')) {
                  return (
                    <div className='catListItem'>
                      <button className={`catSelector ${formats.indexOf(format.value) !== -1 && 'catSelected'}`} onClick={() => {
                        toggleFormat(format.value);
                      }}></button>
                      <div className='catSelectorLabel'>{format.label}</div>
                    </div>
                  )
                }
              }
              )}
            </div>
          </div>
          <div className='sidebarFilter'>
            <div className='pseudoLabel'>Categories</div>
            <div className='catList'>
              {allCategories.map((cat) =>
                <div className='catListItem'>
                  <button className={`catSelector ${categories.indexOf(cat.value) !== -1 && 'catSelected'}`} onClick={() => {
                    toggleCat(cat.value);
                  }}></button>
                  <div className='catSelectorLabel'>{cat.label}</div>
                </div>
              )}
            </div>
          </div>
          <div className='sidebarFilter'><RangeControl
            label='Pagecount'
            value={[minPages, maxPages]}
            min={1}
            max={2000}
            step={10}
            change={(value) => {
              setMinPages(value[0]);
              setMaxPages(value[1]);
            }}
          /></div>
          <div className='sidebarFilter'>
            <div className='pseudoLabel'>Quick Filters</div>
            <div className='catList'>
              <div className='catListItem'>
                <button className={`catSelector ${onSale && 'catSelected'}`} onClick={() => {
                  setOnSale(!onSale);
                }}></button>
                <div className='catSelectorLabel'>On sale</div>
              </div>
              <div className='catListItem'>
                <button className={`catSelector ${inStockNow && 'catSelected'}`} onClick={() => {
                  setInStockNow(!inStockNow);
                }}></button>
                <div className='catSelectorLabel'>In-stock now</div>
              </div>
              <div className='catListItem'>
                <button className={`catSelector ${used && 'catSelected'}`} onClick={() => {
                  setUsed(!used);
                }}></button>
                <div className='catSelectorLabel'>Available used</div>
              </div>
              <div className='catListItem'>
                <button className={`catSelector ${hasPreview && 'catSelected'}`} onClick={() => {
                  setHasPreview(!hasPreview);
                }}></button>
                <div className='catSelectorLabel'>Title has preview</div>
              </div>
              <div className='catListItem'>
                <button className={`catSelector ${lastOne && 'catSelected'}`} onClick={() => {
                  setLastOne(!lastOne);
                }}></button>
                <div className='catSelectorLabel'>One Copy Left</div>
              </div>
            </div>
          </div>

          <div>
            <button className='buttonSecondary' onClick={exportView}>Export View</button>
          </div>

        </div>
        <div>
          <div className='lowerGrid'>
            {loading && <GhostGrid />}
            {!loading && products.length == 0 && <div>No products matched your request.</div>}
            {products.map((product, index) =>
              <ProductListItem used={used} key={product._id} product={product} featured={index === featured} context='allBooks' />
            )}
          </div>
          <Loader fetching={loading} getMore={getProducts} hasMore={hasMore} page={page} />
        </div>
      </div>
    </div>
  )
}
